import type React from "react";
import {
  composeRenderProps,
  Checkbox as RACCheckbox,
  CheckboxGroup as RACCheckboxGroup,
  type CheckboxGroupProps as RACCheckboxGroupProps,
  type CheckboxProps as RACCheckboxProps,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { DescriptionProvider, useDescription } from "@/components/ui/field";
import {
  cn,
  composeTailwindRenderProps,
  focusRing,
} from "@/components/ui/utils";

export function CheckboxGroup(props: RACCheckboxGroupProps) {
  return (
    <RACCheckboxGroup
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "flex flex-col gap-y-2",
      )}
    />
  );
}

const checkboxStyles = tv({
  base: "relative flex gap-2 items-center group text-sm transition",
  variants: {
    isDisabled: {
      true: "opacity-50 cursor-not-allowed",
    },
  },
});

const indicatorStyles = tv({
  extend: focusRing,
  base: "peer grid size-4 shrink-0 place-content-center rounded border border-input shadow-xs shadow-black/5",
  variants: {
    isSelected: {
      true: "border-primary bg-primary text-primary-foreground",
    },
    isInvalid: {
      true: "border-destructive",
    },
  },
});

type CheckboxProps = RACCheckboxProps & {
  ref?: React.Ref<HTMLLabelElement>;
};

export function Checkbox({
  className,
  ref,
  children,
  ...props
}: CheckboxProps) {
  const descriptionContext = useDescription();
  return (
    <RACCheckbox
      {...props}
      ref={ref}
      aria-describedby={descriptionContext?.["aria-describedby"]}
      className={composeRenderProps(className, (className, renderProps) =>
        checkboxStyles({ className, ...renderProps }),
      )}
    >
      {composeRenderProps(
        children,
        (
          children,
          { isSelected, isIndeterminate, isFocusVisible, ...renderProps },
        ) => {
          return (
            <>
              <div
                className={cn(
                  indicatorStyles({
                    isSelected: isSelected || isIndeterminate,
                    ...renderProps,
                  }),
                  isFocusVisible && "outline-2",
                )}
                data-ui="indicator"
              >
                {isIndeterminate && (
                  <svg
                    width="9"
                    height="9"
                    viewBox="0 0 9 9"
                    fill="currentcolor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0.75 4.5C0.75 4.08579 1.08579 3.75 1.5 3.75H7.5C7.91421 3.75 8.25 4.08579 8.25 4.5C8.25 4.91421 7.91421 5.25 7.5 5.25H1.5C1.08579 5.25 0.75 4.91421 0.75 4.5Z"
                    />
                  </svg>
                )}
                {isSelected && (
                  <svg
                    width="9"
                    height="9"
                    viewBox="0 0 9 9"
                    fill="currentcolor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M8.53547 0.62293C8.88226 0.849446 8.97976 1.3142 8.75325 1.66099L4.5083 8.1599C4.38833 8.34356 4.19397 8.4655 3.9764 8.49358C3.75883 8.52167 3.53987 8.45309 3.3772 8.30591L0.616113 5.80777C0.308959 5.52987 0.285246 5.05559 0.563148 4.74844C0.84105 4.44128 1.31533 4.41757 1.62249 4.69547L3.73256 6.60459L7.49741 0.840706C7.72393 0.493916 8.18868 0.396414 8.53547 0.62293Z"
                    />
                  </svg>
                )}
              </div>

              {children}
            </>
          );
        },
      )}
    </RACCheckbox>
  );
}

export function CheckboxField({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <DescriptionProvider>
      <div
        {...props}
        data-ui="field"
        className={cn(
          "group flex flex-col gap-y-1",
          "*:slot-description:ps-6",
          "has-slot-description:*:[label]:font-medium",
          "has-[label[data-disabled]]:*:slot-description:opacity-50",
          className,
        )}
      />
    </DescriptionProvider>
  );
}
